import type { BreakingTier } from "./ai/breakingTier";
import { Cell } from "./cell";
import type { DistanceMap } from "./distanceMap";
import { DistanceMapCache } from "./distanceMapCache";
import type { Effect } from "./effects/effect";
import { MovementEffect } from "./effects/movementEffect";
import type { Event } from "./events/event";
import { createGameFlags, type GameFlags } from "./gameFlags";
import type { GameStats } from "./gameStats";
import {
	isMusicOverrider,
	isProcessableThing,
	isMultiPassProcessableThing,
	isOnAddToBoard,
	isOnPlayerArrive,
	isOnPlayerDriveOver,
	isPostDeserializable,
	type OnPlayerArrive,
	type ProcessableThing,
} from "./interfaces";
import { Mob } from "./mob";
import { MusicId } from "./musicId";
import { Passability, type PassabilityRule } from "./passability";
import { PhysicalThing } from "./physicalThing";
import { Player } from "./player";
import { getMoveDirection, getShootDirection, PlayerCommand } from "./playerCommand";
import { serializeThing, deserializeThing, serializeXY, deserializeXY } from "./serialization";
import { StatsTracker } from "./statsTracker";
import type { Thing } from "./thing";
import { LevelChangerThing } from "./things/levelChangerThing";
import { MultiTileThingCore } from "./things/multitile/multiTileThingCore";
import type { VendingMachine } from "./things/vendingMachine";
import { XY } from "./xy";

interface ProcessableEntry {
	pass: number;
	thing: ProcessableThing;
}

// Default radii (in cells) for the player's light circle when `Board.darkness > 0`.
// The inner radius is fully lit; alpha ramps to `darkness` at the outer radius.
export const DEFAULT_LIGHT_INNER_RADIUS = 0.5;
export const DEFAULT_LIGHT_OUTER_RADIUS = 5;

export function cleanGoldAmount(amount: number): number {
	if (amount <= 0) return 0;
	if (amount < 5) return 1;
	return Math.floor(amount / 5) * 5;
}

export class Board {
	readonly width: number;
	readonly height: number;
	readonly cells: Cell[][];
	private thingList: Thing[] = [];
	player: Player | null = null;
	gameFlags: GameFlags = createGameFlags();
	private events: Event[] = [];
	turnCount: number = 0;
	processingTurn: boolean = false;
	turnAbortedByPlayer: boolean = false;
	bumpActionOccurred: boolean = false;
	interactionFailed: boolean = false;
	lastTurnAborted: boolean = false;
	currentProcessingPass: number = 0;
	private removalQueue: Thing[] = [];
	private runnables: Map<number, Array<() => void>> = new Map();
	private distanceMapCache = new DistanceMapCache();
	stats: StatsTracker = new StatsTracker();
	game: {
		addEvent(event: Event): void;
		gameFlags: GameFlags;
		stats: GameStats;
		markWeaponHintShown(weaponKey: string): boolean;
		emitHint(hint: { id: string; stringKey: string }): void;
	} | null = null;
	levelChanger: { beginLevelTransition(n: number): void } | null = null;
	readonly terrainVisualIds: number[][];
	defaultFloor: string | null = null;
	areaName: string | null = null;
	musicId: MusicId | null = null;
	darkness: number = 0;
	lightInnerRadius: number = DEFAULT_LIGHT_INNER_RADIUS;
	lightOuterRadius: number = DEFAULT_LIGHT_OUTER_RADIUS;
	dynamicGoldBudget: number = 0;
	dynamicGoldSpent: number = 0;
	initialSpawnXY: XY | null = null;
	lastTurnEffects: Effect[] = [];
	// Per-turn board delta the renderer reads to show pre-turn state during pre-effects.
	// capturingTurnChanges stays set through processRemovalQueue (processingTurn doesn't).
	lastTurnRemovals: PhysicalThing[] = [];
	lastTurnAdditions: Set<PhysicalThing> = new Set();
	private capturingTurnChanges: boolean = false;
	pendingSignText: { xy: XY; text: string } | null = null;
	pendingVendingMachine: VendingMachine | null = null;
	private currentPrompts: string[] = [];
	private allXYs: XY[] | null = null;

	constructor(width: number, height: number, rng: () => number = Math.random) {
		this.width = width;
		this.height = height;
		this.cells = [];
		this.terrainVisualIds = [];
		for (let x = 0; x < width; x++) {
			this.cells[x] = [];
			this.terrainVisualIds[x] = [];
			for (let y = 0; y < height; y++) {
				this.cells[x][y] = new Cell();
				this.terrainVisualIds[x][y] = Math.floor(rng() * 7);
			}
		}
	}

	setGame(game: {
		addEvent(event: Event): void;
		gameFlags: GameFlags;
		stats: GameStats;
		markWeaponHintShown(weaponKey: string): boolean;
		emitHint(hint: { id: string; stringKey: string }): void;
	}): void {
		this.game = game;
		this.gameFlags = game.gameFlags;
		this.stats.setParent(game.stats.tracker);
	}

	beginLevelTransition(levelNumber: number): void {
		if (!this.levelChanger) throw new Error("No levelChanger set on board");
		this.levelChanger.beginLevelTransition(levelNumber);
	}

	spendGoldBudget(baseGoldValue: number): number {
		if (this.dynamicGoldBudget <= 0) return 0;
		const remaining = this.dynamicGoldBudget - this.dynamicGoldSpent;
		if (remaining <= 0) return 0;
		const fraction = remaining / this.dynamicGoldBudget;
		const raw = Math.round(baseGoldValue * fraction);
		const goldAmount = cleanGoldAmount(raw);
		this.dynamicGoldSpent += goldAmount;
		return goldAmount;
	}

	addThing(thing: PhysicalThing, skipOnAdd: boolean = false): void {
		if (!this.isInBounds(thing.xy)) {
			console.warn(
				`addThing out of bounds: (${thing.xy.x}, ${thing.xy.y}) on ${this.width}x${this.height} board — ignoring`,
			);
			return;
		}
		this.getCell(thing.xy).addThing(thing);
		this.thingList.push(thing);
		thing.board = this;
		if (this.capturingTurnChanges) this.lastTurnAdditions.add(thing);
		if (thing instanceof Player) this.player = thing;
		if (!skipOnAdd && isOnAddToBoard(thing)) {
			thing.onAddToBoard();
		}
	}

	addNonPhysicalThing(thing: Thing, skipOnAdd: boolean = false): void {
		this.thingList.push(thing);
		thing.board = this;
		if (!skipOnAdd && isOnAddToBoard(thing)) {
			thing.onAddToBoard();
		}
	}

	removeThing(thing: Thing): void {
		if (thing instanceof PhysicalThing) {
			this.getCell(thing.xy).removeThing(thing);
			if (thing instanceof Player) this.player = null;
		}
		const idx = this.thingList.indexOf(thing);
		if (idx !== -1) this.thingList.splice(idx, 1);
		thing.active = false;
		thing.board = null;
		if (this.capturingTurnChanges && idx !== -1 && thing instanceof PhysicalThing) {
			// Added-and-removed same turn (e.g. a projectile) cancels out, counts as neither.
			if (this.lastTurnAdditions.has(thing)) {
				this.lastTurnAdditions.delete(thing);
			} else {
				this.lastTurnRemovals.push(thing);
			}
		}
		if (idx !== -1) thing.onRemoveFromBoard(this);
	}

	removeAtEndOfTurn(thing: Thing): void {
		if (!thing.active) return;
		thing.active = false;
		this.removalQueue.push(thing);
	}

	moveThing(thing: PhysicalThing, to: XY): void {
		if (!this.isInBounds(thing.xy)) {
			throw new Error(
				`moveThing source out of bounds: (${thing.xy.x}, ${thing.xy.y}) on ${this.width}x${this.height} board`,
			);
		}
		if (!this.isInBounds(to)) {
			throw new Error(`moveThing destination out of bounds: (${to.x}, ${to.y}) on ${this.width}x${this.height} board`);
		}
		this.getCell(thing.xy).removeThing(thing);
		thing.xy = to;
		this.getCell(to).addThing(thing);

		if (thing.active && thing instanceof Player) {
			for (const t of this.getThingsAt(to)) {
				if (t.active && isOnPlayerDriveOver(t)) {
					t.onPlayerDriveOver();
				}
			}
		}
	}

	runLater(fn: () => void): void {
		this.runLaterOnPass(this.currentProcessingPass, fn);
	}

	runLaterOnPass(pass: number, fn: () => void): void {
		if (!this.processingTurn) return;
		if (this.currentProcessingPass > pass) return;

		let list = this.runnables.get(pass);
		if (!list) {
			list = [];
			this.runnables.set(pass, list);
		}
		list.push(fn);
	}

	getCell(xy: XY): Cell {
		if (!this.isInBounds(xy)) {
			throw new Error(`getCell out of bounds: (${xy.x}, ${xy.y}) on ${this.width}x${this.height} board`);
		}
		return this.cells[xy.x][xy.y];
	}

	isInBounds(xy: XY): boolean {
		return xy.x >= 0 && xy.x < this.width && xy.y >= 0 && xy.y < this.height;
	}

	isPassable(xy: XY): boolean {
		return this.isInBounds(xy) && !this.getCell(xy).isBlocked();
	}

	addEvent(event: Event): void {
		if (this.game) {
			this.game.addEvent(event);
		} else {
			this.events.push(event);
		}
	}

	getEvents(): Event[] {
		return this.events;
	}

	emitPrompt(text: string): void {
		this.currentPrompts.push(text);
	}

	getCurrentPrompts(): readonly string[] {
		return this.currentPrompts;
	}

	requestShowSignText(xy: XY, text: string): void {
		this.pendingSignText = { xy, text };
	}

	consumePendingSignText(): { xy: XY; text: string } | null {
		const v = this.pendingSignText;
		this.pendingSignText = null;
		return v;
	}

	requestOpenVendingMachine(machine: VendingMachine): void {
		this.pendingVendingMachine = machine;
	}

	consumeVendingMachineRequest(): VendingMachine | null {
		const v = this.pendingVendingMachine;
		this.pendingVendingMachine = null;
		return v;
	}

	getCachedDistanceMap(target: XY, rule: PassabilityRule = Passability.WALKING): DistanceMap {
		return this.distanceMapCache.getDistanceMap(this, target, rule);
	}

	getDistanceMapToPlayer(): DistanceMap | null {
		if (!this.player) return null;
		return this.getCachedDistanceMap(this.player.xy, Passability.WALKING);
	}

	getDistanceMapToPlayerIncludingDoors(): DistanceMap | null {
		if (!this.player) return null;
		return this.getCachedDistanceMap(this.player.xy, Passability.THROUGH_DOORS);
	}

	getDistanceMapForBreakingTier(tier: BreakingTier): DistanceMap | null {
		if (!this.player) return null;
		return this.getCachedDistanceMap(this.player.xy, Passability.breakingTier(tier));
	}

	getDistanceMapToPlayerForFlyingThings(): DistanceMap | null {
		if (!this.player) return null;
		return this.getCachedDistanceMap(this.player.xy, Passability.FLYING);
	}

	getThingsAt(xy: XY): PhysicalThing[] {
		return this.getCell(xy).getThings();
	}

	getNeighbors(xy: XY): XY[] {
		return xy.getNeighbors().filter((n) => this.isInBounds(n));
	}

	hasMob(xy: XY): boolean {
		return this.getThingsAt(xy).some((t) => t instanceof Mob && t.active && !t.isFrozen());
	}

	getAllThings(): Thing[] {
		return this.thingList;
	}

	getPhysicalThings(): PhysicalThing[] {
		return this.thingList.filter((t): t is PhysicalThing => t instanceof PhysicalThing);
	}

	findLevelChangerTo(destinationLevel: number): LevelChangerThing | null {
		for (const thing of this.getPhysicalThings()) {
			if (thing instanceof LevelChangerThing && thing.destinationLevel === destinationLevel) {
				return thing;
			}
		}
		return null;
	}

	getTerrainVisualId(xy: XY): number {
		return this.terrainVisualIds[xy.x][xy.y];
	}

	addEffect(effect: Effect): void {
		if (effect instanceof MovementEffect) {
			// Chain successive moves of the same thing within a turn so they animate sequentially.
			const priorOrders = this.lastTurnEffects
				.filter((e): e is MovementEffect => e instanceof MovementEffect && e.thing === effect.thing)
				.map((e) => e.getAnimationOrder());
			if (priorOrders.length > 0) {
				const maxOrder = Math.max(...priorOrders);
				if (maxOrder >= effect.getAnimationOrder()) {
					effect.setAnimationOrder(maxOrder + 1);
				}
			}
		}
		this.lastTurnEffects.push(effect);
	}

	getLastTurnEffects(): Effect[] {
		return this.lastTurnEffects;
	}

	isCellEmpty(xy: XY): boolean {
		return this.getCell(xy).getThings().length === 0;
	}

	getAllXYs(): XY[] {
		if (!this.allXYs) {
			this.allXYs = [];
			for (let x = 0; x < this.width; x++) {
				for (let y = 0; y < this.height; y++) {
					this.allXYs.push(new XY(x, y));
				}
			}
		}
		return this.allXYs;
	}

	getThingOfClass<T extends PhysicalThing>(clazz: new (...args: any[]) => T, xy: XY): T | null {
		return (this.getThingsAt(xy).find((t) => t instanceof clazz) as T) ?? null;
	}

	getThingsOfType<T extends PhysicalThing>(xy: XY, guard: (t: PhysicalThing) => t is T): T[] {
		return this.getThingsAt(xy).filter(guard);
	}

	getFirstOfType<T extends PhysicalThing>(xy: XY, guard: (t: PhysicalThing) => t is T): T | null {
		return this.getThingsAt(xy).find(guard) ?? null;
	}

	hasThingOfType<T extends PhysicalThing>(xy: XY, guard: (t: PhysicalThing) => t is T): boolean {
		return this.getThingsAt(xy).some(guard);
	}

	getAllOfType<T extends Thing>(guard: (t: Thing) => t is T): T[] {
		return this.thingList.filter(guard);
	}

	getFirstOfTypeOnBoard<T extends Thing>(guard: (t: Thing) => t is T): T | null {
		return this.thingList.find(guard) ?? null;
	}

	hasThingOfTypeOnBoard<T extends Thing>(guard: (t: Thing) => t is T): boolean {
		return this.thingList.some(guard);
	}

	serialize(): Record<string, unknown> {
		const things = this.thingList.filter((t) => t.active && t.shouldSerialize()).map((t) => serializeThing(t));
		const result: Record<string, unknown> = {
			width: this.width,
			height: this.height,
			things,
			turnCount: this.turnCount,
			terrainVisualIds: this.terrainVisualIds,
			defaultFloor: this.defaultFloor,
			areaName: this.areaName,
			musicId: this.musicId,
			darkness: this.darkness,
			lightInnerRadius: this.lightInnerRadius,
			lightOuterRadius: this.lightOuterRadius,
			dynamicGoldBudget: this.dynamicGoldBudget,
			dynamicGoldSpent: this.dynamicGoldSpent,
			stats: this.stats.serialize(),
		};
		if (this.initialSpawnXY) {
			result.initialSpawnXY = serializeXY(this.initialSpawnXY);
		}
		return result;
	}

	static deserialize(data: any): Board {
		const board = new Board(data.width, data.height);
		board.turnCount = data.turnCount;
		if (data.terrainVisualIds) {
			for (let x = 0; x < board.width; x++) {
				for (let y = 0; y < board.height; y++) {
					board.terrainVisualIds[x][y] = data.terrainVisualIds[x][y];
				}
			}
		}
		board.defaultFloor = data.defaultFloor ?? null;
		board.areaName = data.areaName ?? null;
		board.musicId = (data.musicId as MusicId | null | undefined) ?? null;
		board.darkness = data.darkness ?? 0;
		board.lightInnerRadius = data.lightInnerRadius ?? DEFAULT_LIGHT_INNER_RADIUS;
		board.lightOuterRadius = data.lightOuterRadius ?? DEFAULT_LIGHT_OUTER_RADIUS;
		board.dynamicGoldBudget = data.dynamicGoldBudget ?? 0;
		board.dynamicGoldSpent = data.dynamicGoldSpent ?? 0;
		board.initialSpawnXY = data.initialSpawnXY ? deserializeXY(data.initialSpawnXY) : null;
		if (data.stats) {
			board.stats = StatsTracker.deserialize(data.stats);
		}
		for (const thingData of data.things) {
			const thing = deserializeThing(thingData);
			if (thing instanceof MultiTileThingCore) {
				// Cores don't occupy cells — parts handle physical presence
				board.addNonPhysicalThing(thing, true);
			} else if (thing instanceof PhysicalThing) {
				board.addThing(thing, true);
			} else {
				board.addNonPhysicalThing(thing, true);
			}
		}
		board.postDeserialize();
		return board;
	}

	private postDeserialize(): void {
		for (const thing of [...this.thingList]) {
			if (isPostDeserializable(thing)) {
				thing.postDeserialize(this);
			}
		}
	}

	dispatchPlayerArrival(): void {
		const handlers = this.thingList
			.filter((t): t is Thing & OnPlayerArrive => t.active && isOnPlayerArrive(t))
			.sort((a, b) => a.getArrivePriority() - b.getArrivePriority());
		for (const h of handlers) {
			if (h.active) h.onPlayerArrive(this);
		}
	}

	resolveMusicId(): MusicId | null {
		for (const thing of this.thingList) {
			if (!thing.active) continue;
			if (isMusicOverrider(thing)) {
				const override = thing.getMusicOverride();
				if (override !== null) return override;
			}
		}
		return this.musicId;
	}

	processTurn(): void {
		this.events = [];
		this.lastTurnEffects = [];
		this.lastTurnRemovals = [];
		this.lastTurnAdditions = new Set();
		this.currentPrompts = [];
		this.processingTurn = true;
		this.capturingTurnChanges = true;
		this.turnAbortedByPlayer = false;
		this.bumpActionOccurred = false;
		this.interactionFailed = false;
		this.lastTurnAborted = false;
		this.currentProcessingPass = 0;

		const entries = this.buildProcessableEntries();

		let turnAborted = false;

		for (const entry of entries) {
			if (entry.pass > this.currentProcessingPass) {
				for (let i = this.currentProcessingPass; i < entry.pass; i++) {
					this.executeRunnables(i);
				}
				this.currentProcessingPass = entry.pass;
			}

			const thing = entry.thing as unknown as { active?: boolean };
			if (thing.active === false) continue;

			if (entry.thing instanceof PhysicalThing) {
				const beforeXY = entry.thing.xy;
				const playerCommand = entry.thing instanceof Player ? entry.thing.pendingCommand : null;
				entry.thing.processTurn(this);
				const afterXY = entry.thing.xy;
				if (!beforeXY.equals(afterXY) && this.getCell(beforeXY).hasThing(entry.thing)) {
					this.moveBetweenCells(entry.thing, beforeXY, afterXY);
					if (entry.thing instanceof Player) {
						for (const t of this.getThingsAt(afterXY)) {
							if (t.active && isOnPlayerDriveOver(t)) {
								t.onPlayerDriveOver();
							}
						}
					}
				}
				if (entry.thing instanceof Player && playerCommand !== null) {
					// Abort turn if player tried to move but didn't (and no bump action succeeded)
					if (getMoveDirection(playerCommand) !== null && beforeXY.equals(afterXY) && !this.bumpActionOccurred) {
						turnAborted = true;
						break;
					}
					// Abort turn if weapon signaled failure
					if (getShootDirection(playerCommand) !== null && this.turnAbortedByPlayer) {
						turnAborted = true;
						break;
					}
					// Abort turn if use-item signaled failure
					if (playerCommand === PlayerCommand.USE_ITEM && this.turnAbortedByPlayer) {
						turnAborted = true;
						break;
					}
					// Abort turn if interact found no valid target
					if (playerCommand === PlayerCommand.INTERACT && this.interactionFailed) {
						turnAborted = true;
						break;
					}
					// Abort turn if auto-shoot found no target or weapon signaled failure
					if (playerCommand === PlayerCommand.AUTO_SHOOT && this.turnAbortedByPlayer) {
						turnAborted = true;
						break;
					}
				}
			} else {
				entry.thing.processTurn(this);
			}
		}

		if (!turnAborted) {
			this.executeRunnables(this.currentProcessingPass);
		}
		this.processingTurn = false;
		this.lastTurnAborted = turnAborted;
		if (!turnAborted) {
			this.processRemovalQueue();
			this.turnCount++;
			this.player?.onTurnConsumed();
		}
		this.capturingTurnChanges = false;
		this.runnables.clear();
		this.afterTurn();
	}

	private afterTurn(): void {
		if (this.player && this.player.isAutoMoving() && this.player.isInDanger()) {
			this.player.setAutoMoving(false);
		}
	}

	private buildProcessableEntries(): ProcessableEntry[] {
		const entries: ProcessableEntry[] = [];

		for (const thing of this.thingList) {
			if (!isProcessableThing(thing)) continue;

			if (isMultiPassProcessableThing(thing)) {
				for (const pass of thing.getPassesToRun()) {
					entries.push({ pass, thing });
				}
			} else {
				entries.push({ pass: 0, thing });
			}
		}

		entries.sort((a, b) => {
			if (a.pass !== b.pass) return a.pass - b.pass;
			return a.thing.getProcessPriority() - b.thing.getProcessPriority();
		});

		return entries;
	}

	private moveBetweenCells(thing: PhysicalThing, from: XY, to: XY): void {
		if (!this.isInBounds(from)) {
			throw new Error(
				`moveBetweenCells source out of bounds: (${from.x}, ${from.y}) on ${this.width}x${this.height} board`,
			);
		}
		if (!this.isInBounds(to)) {
			throw new Error(
				`moveBetweenCells destination out of bounds: (${to.x}, ${to.y}) on ${this.width}x${this.height} board`,
			);
		}
		this.getCell(from).removeThing(thing);
		this.getCell(to).addThing(thing);
	}

	private executeRunnables(pass: number): void {
		const list = this.runnables.get(pass);
		if (!list) return;
		for (const fn of list) {
			fn();
		}
	}

	private processRemovalQueue(): void {
		for (const thing of this.removalQueue) {
			this.removeThing(thing);
		}
		this.removalQueue = [];
	}
}
